/**
 * Universal Table Redux Slice
 *
 * Manages global table state (column visibility, column order, row selection, pagination)
 * for any table identified by a unique tableId.
 *
 * CRITICAL: This slice stores ONLY serializable state. TanStack Table instances
 * must NOT be stored here. Components must resolve functional updaters before
 * dispatching actions.
 *
 * Data Flow:
 * 1. TanStack Table emits state change (may be a function)
 * 2. Component resolves function to concrete value
 * 3. Component dispatches serialized state to Redux
 * 4. Redux updates global state
 * 5. Component reads state and updates TanStack Table
 *
 * PAGINATION RESET:
 * When filter-related actions fire from searchAndFilterSlice (column filters,
 * date filters, extra filters), this slice automatically resets pageIndex to 0
 * for all tables registered to the affected pageName. Tables register their
 * pageName↔tableId mapping via registerPageMapping on mount.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  SortingState,
  VisibilityState,
  ColumnOrderState,
  PaginationState,
  RowSelectionState,
} from '@tanstack/react-table';
import { setColumnFilters, setDateFilters, setExtraFilters } from 'app/store/filtering/searchAndFilterSlice';
import {
  applyTransactionColumnFilters,
  applyTransactionDateFilter,
  clearTransactionColumnFilters,
} from 'app/store/filtering/transactionFiltersThunks';
import { applyCardsColumnFilters } from 'app/store/filtering/cardsFiltersThunks';
import { applyUsersColumnFilters, clearUsersColumnFilters } from 'app/store/filtering/userFiltersThunks';
import {
  applyCardGroupsColumnFilters,
  clearCardGroupsColumnFilters,
} from 'app/store/filtering/cardGroupFiltersThunks';
import {
  applyProjectGroupsColumnFilters,
  clearProjectGroupsColumnFilters,
} from 'app/store/filtering/projectGroupFiltersThunks';

// Types

/**
 * State for a single table instance
 */
export interface TableInstanceState {
  sorting: SortingState;
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
  rowSelection: RowSelectionState;
  pagination: PaginationState;
}

/**
 * Root table state - keyed by tableId for multi-table support
 */
export interface TableState {
  tables: Record<string, TableInstanceState>;
  /** Maps pageName → tableIds so filter changes auto-reset pagination */
  pageNameToTableIds: Record<string, string[]>;
}

/**
 * Action payload for updating any table state property
 */
interface UpdateTableStatePayload<K extends keyof TableInstanceState> {
  tableId: string;
  state: TableInstanceState[K];
}

// Initial State

const defaultTableState: TableInstanceState = {
  sorting: [],
  columnVisibility: {},
  columnOrder: [],
  rowSelection: {},
  pagination: {
    pageIndex: 0,
    pageSize: 25,
  },
};

const initialState: TableState = {
  tables: {},
  pageNameToTableIds: {},
};

// Helpers

/**
 * Reset pageIndex to 0 for all tables registered to a pageName.
 * Preserves pageSize.
 */
function resetPaginationForPage(state: TableState, pageName: string) {
  const tableIds = state.pageNameToTableIds[pageName];
  if (!tableIds) return;
  for (const tableId of tableIds) {
    if (state.tables[tableId]) {
      state.tables[tableId].pagination.pageIndex = 0;
    }
  }
}

// Slice

export const tableSlice = createSlice({
  name: 'table',
  initialState,
  reducers: {
    /**
     * Initialize a table with default state
     * Call this when mounting a table to ensure state exists
     */
    initializeTable: (state, action: PayloadAction<string>) => {
      const tableId = action.payload;
      if (!state.tables[tableId]) {
        state.tables[tableId] = { ...defaultTableState };
      }
    },

    /**
     * Update sorting state for a table
     * IMPORTANT: Caller must resolve TanStack functional updaters before dispatching
     */
    setSorting: (state, action: PayloadAction<UpdateTableStatePayload<'sorting'>>) => {
      const { tableId, state: sortingState } = action.payload;
      if (!state.tables[tableId]) {
        state.tables[tableId] = { ...defaultTableState };
      }
      state.tables[tableId].sorting = sortingState;
    },

    /**
     * Update column visibility state for a table
     * IMPORTANT: Caller must resolve TanStack functional updaters before dispatching
     */
    setColumnVisibility: (state, action: PayloadAction<UpdateTableStatePayload<'columnVisibility'>>) => {
      const { tableId, state: visibilityState } = action.payload;
      if (!state.tables[tableId]) {
        state.tables[tableId] = { ...defaultTableState };
      }
      state.tables[tableId].columnVisibility = visibilityState;
    },

    /**
     * Update column order state for a table
     * IMPORTANT: Caller must resolve TanStack functional updaters before dispatching
     */
    setColumnOrder: (state, action: PayloadAction<UpdateTableStatePayload<'columnOrder'>>) => {
      const { tableId, state: orderState } = action.payload;
      if (!state.tables[tableId]) {
        state.tables[tableId] = { ...defaultTableState };
      }
      state.tables[tableId].columnOrder = orderState;
    },

    /**
     * Update row selection state for a table
     * IMPORTANT: Caller must resolve TanStack functional updaters before dispatching
     */
    setRowSelection: (state, action: PayloadAction<UpdateTableStatePayload<'rowSelection'>>) => {
      const { tableId, state: rowSelectionState } = action.payload;
      if (!state.tables[tableId]) {
        state.tables[tableId] = { ...defaultTableState };
      }
      state.tables[tableId].rowSelection = rowSelectionState;
    },

    /**
     * Update pagination state for a table
     * IMPORTANT: Caller must resolve TanStack functional updaters before dispatching
     */
    setPagination: (state, action: PayloadAction<UpdateTableStatePayload<'pagination'>>) => {
      const { tableId, state: paginationState } = action.payload;
      if (!state.tables[tableId]) {
        state.tables[tableId] = { ...defaultTableState };
      }
      state.tables[tableId].pagination = paginationState;
    },

    /**
     * Reset a table to default state
     * Useful for cleanup or resetting filters
     */
    resetTable: (state, action: PayloadAction<string>) => {
      const tableId = action.payload;
      state.tables[tableId] = { ...defaultTableState };
    },

    /**
     * Remove a table from state entirely
     * Call this on unmount if you want to free memory
     */
    removeTable: (state, action: PayloadAction<string>) => {
      const tableId = action.payload;
      delete state.tables[tableId];
    },

    /**
     * Register a pageName↔tableId mapping so filter changes
     * for that pageName automatically reset this table's pagination.
     * Call on mount.
     */
    registerPageMapping: (state, action: PayloadAction<{ pageName: string; tableId: string }>) => {
      const { pageName, tableId } = action.payload;
      if (!state.pageNameToTableIds[pageName]) {
        state.pageNameToTableIds[pageName] = [];
      }
      if (!state.pageNameToTableIds[pageName].includes(tableId)) {
        state.pageNameToTableIds[pageName].push(tableId);
      }
    },

    /**
     * Unregister a pageName↔tableId mapping.
     * Call on unmount.
     */
    unregisterPageMapping: (state, action: PayloadAction<{ pageName: string; tableId: string }>) => {
      const { pageName, tableId } = action.payload;
      if (state.pageNameToTableIds[pageName]) {
        state.pageNameToTableIds[pageName] = state.pageNameToTableIds[pageName].filter(
          (id) => id !== tableId
        );
        if (state.pageNameToTableIds[pageName].length === 0) {
          delete state.pageNameToTableIds[pageName];
        }
      }
    },
  },

  extraReducers: (builder) => {
    // ── Sync actions from searchAndFilterSlice that reset pagination ──

    builder.addCase(setColumnFilters, (state, action) => {
      resetPaginationForPage(state, action.payload.pageName);
    });
    builder.addCase(setDateFilters, (state, action) => {
      resetPaginationForPage(state, action.payload.page);
    });
    builder.addCase(setExtraFilters, (state, action) => {
      resetPaginationForPage(state, action.payload.pageName);
    });

    // ── Filter thunks (.pending) that reset pagination ──

    const filterThunks = [
      applyTransactionColumnFilters,
      applyTransactionDateFilter,
      clearTransactionColumnFilters,
      applyCardsColumnFilters,
      applyUsersColumnFilters,
      clearUsersColumnFilters,
      applyCardGroupsColumnFilters,
      clearCardGroupsColumnFilters,
      applyProjectGroupsColumnFilters,
      clearProjectGroupsColumnFilters,
    ];

    filterThunks.forEach((thunk) => {
      builder.addCase(thunk.pending, (state, action) => {
        const pageName = action.meta.arg.pageName;
        resetPaginationForPage(state, pageName);
      });
    });
  },
});

// Actions

export const {
  initializeTable,
  setSorting,
  setColumnVisibility,
  setColumnOrder,
  setRowSelection,
  setPagination,
  resetTable,
  removeTable,
  registerPageMapping,
  unregisterPageMapping,
} = tableSlice.actions;

// Selectors

/**
 * Select table state by tableId
 * Returns default state if table doesn't exist
 */
export const selectTableState = (
  state: { universalTable: TableState },
  tableId: string
): TableInstanceState => {
  return state.universalTable.tables[tableId] || defaultTableState;
};

/**
 * Select sorting state for a specific table
 */
export const selectSorting = (state: { universalTable: TableState }, tableId: string): SortingState => {
  return selectTableState(state, tableId).sorting;
};

/**
 * Select column visibility for a specific table
 */
export const selectColumnVisibility = (
  state: { universalTable: TableState },
  tableId: string
): VisibilityState => {
  return selectTableState(state, tableId).columnVisibility;
};

/**
 * Select column order for a specific table
 */
export const selectColumnOrder = (
  state: { universalTable: TableState },
  tableId: string
): ColumnOrderState => {
  return selectTableState(state, tableId).columnOrder;
};

/**
 * Select row selection state for a specific table
 */
export const selectRowSelection = (
  state: { universalTable: TableState },
  tableId: string
): RowSelectionState => {
  return selectTableState(state, tableId).rowSelection;
};

/**
 * Select pagination state for a specific table
 */
export const selectPagination = (state: { universalTable: TableState }, tableId: string): PaginationState => {
  return selectTableState(state, tableId).pagination;
};

// Reducer Export

export default tableSlice.reducer;
